import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  GlobalStyles,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useFestival } from '../../context/FestivalContext';
import { formatCheckInDateLabel } from '../../utils/festivalDates';
import { BOOTH_SIZE_LABEL } from '../../utils/boothInfo';
import { formatCurrency, groupFinalRegistrations } from '../../utils/finalRegistrations';

const categoryLabel = (vendor) =>
  vendor.category_slug === 'other' && vendor.category_other_description
    ? `Other — ${vendor.category_other_description}`
    : vendor.category_name;

const addressLines = (vendor) => {
  const cityLine = [
    vendor.city,
    [vendor.state, vendor.zip_code].filter(Boolean).join(' '),
  ].filter(Boolean).join(', ');
  return [vendor.address, cityLine].filter(Boolean);
};

const compareBoothNumbers = (a, b) =>
  String(a || '').localeCompare(String(b || ''), undefined, { numeric: true, sensitivity: 'base' });

const Field = ({ label, children }) => (
  <Box sx={{ mb: 1.5, breakInside: 'avoid' }}>
    <Typography
      variant="caption"
      sx={{ display: 'block', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
    >
      {label}
    </Typography>
    <Typography variant="body1" component="div" sx={{ whiteSpace: 'pre-line' }}>
      {children || '—'}
    </Typography>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle1"
    sx={{ fontWeight: 700, borderBottom: '2px solid', borderColor: 'divider', pb: 0.5, mb: 1.5, mt: 2.5 }}
  >
    {children}
  </Typography>
);

const cellSx = { border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' };

const VendorSheet = ({ vendor, index, total, festivalLabel, eventDate }) => {
  const contactName = `${vendor.first_name || ''} ${vendor.last_name || ''}`.trim();
  return (
    <Box
      className="vendor-sheet"
      sx={{
        bgcolor: 'background.paper',
        p: 5,
        mb: 3,
        boxShadow: 1,
        '@media print': { p: 0, mb: 0, boxShadow: 'none', breakAfter: 'page' },
        '&:last-of-type': { '@media print': { breakAfter: 'auto' } },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography variant="caption" color="text.secondary">{festivalLabel}</Typography>
        <Typography variant="caption" color="text.secondary">Vendor {index + 1} of {total}</Typography>
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
        {vendor.business_name}
      </Typography>
      {categoryLabel(vendor) && (
        <Typography variant="subtitle1" color="text.secondary">{categoryLabel(vendor)}</Typography>
      )}

      <SectionTitle>Booth{vendor.booths.length === 1 ? '' : 's'}</SectionTitle>
      <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 1.5 }}>
        <thead>
          <tr>
            <Box component="th" sx={cellSx}>Booth</Box>
            <Box component="th" sx={cellSx}>Type</Box>
            <Box component="th" sx={cellSx}>Size</Box>
            <Box component="th" sx={cellSx}>Electricity</Box>
          </tr>
        </thead>
        <tbody>
          {vendor.booths.map((booth) => (
            <tr key={booth.booth_number}>
              <Box component="td" sx={{ ...cellSx, fontWeight: 700 }}>{booth.booth_number}</Box>
              <Box component="td" sx={cellSx}>{booth.is_corner ? 'Corner' : 'Regular'}</Box>
              <Box component="td" sx={cellSx}>{BOOTH_SIZE_LABEL}</Box>
              <Box component="td" sx={cellSx}>{booth.needs_electricity ? 'Yes' : 'No'}</Box>
            </tr>
          ))}
        </tbody>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 4 }}>
        <Field label="Check-in">{formatCheckInDateLabel(eventDate, vendor.check_in_date)}</Field>
        <Field label="Amount Paid">{formatCurrency(vendor.amount_paid_cents)}</Field>
      </Box>

      <SectionTitle>Contact</SectionTitle>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 4 }}>
        <Field label="Contact Name">{contactName}</Field>
        <Field label="Phone">{vendor.phone}</Field>
        <Field label="Email">{vendor.email}</Field>
        <Field label="Website">{vendor.website}</Field>
        <Field label="Mailing Address">{addressLines(vendor).join('\n')}</Field>
      </Box>

      <SectionTitle>Exhibit</SectionTitle>
      <Field label="Type of Exhibit">{vendor.exhibit_type}</Field>
      <Field label="Items for Sale">{vendor.description}</Field>
    </Box>
  );
};

const VendorPrintSheets = () => {
  const navigate = useNavigate();
  const { selected: festival } = useFestival();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('business');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await api.get('/admin/final-registrations');
        if (!cancelled) setRegistrations(response.data);
      } catch (err) {
        console.error('Error fetching final registrations:', err);
        if (!cancelled) setError('Failed to load vendors');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [festival?.id]);

  const vendors = useMemo(() => {
    const grouped = groupFinalRegistrations(registrations);
    if (sortBy === 'booth') {
      grouped.forEach((v) => v.booths.sort((a, b) => compareBoothNumbers(a.booth_number, b.booth_number)));
      grouped.sort((a, b) => compareBoothNumbers(a.booths[0]?.booth_number, b.booths[0]?.booth_number));
    }
    return grouped;
  }, [registrations, sortBy]);

  const festivalLabel = festival?.name
    || (festival?.year ? `Gingham Tree Arts and Crafts Festival ${festival.year}` : 'Gingham Tree Arts and Crafts Festival');

  return (
    <Box sx={{ bgcolor: 'grey.100', minHeight: '100vh', '@media print': { bgcolor: 'common.white' } }}>
      <GlobalStyles
        styles={{
          '@page': { size: 'letter', margin: '0.6in' },
          '@media print': {
            body: { backgroundColor: '#fff' },
            '.no-print': { display: 'none !important' },
          },
        }}
      />

      <Box
        className="no-print"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/final-registrations')}>
          Back
        </Button>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Vendor Sheets — {vendors.length} {vendors.length === 1 ? 'vendor' : 'vendors'}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={sortBy}
          onChange={(_e, value) => value && setSortBy(value)}
        >
          <ToggleButton value="business">Sort by business</ToggleButton>
          <ToggleButton value="booth">Sort by booth</ToggleButton>
        </ToggleButtonGroup>
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          disabled={loading || vendors.length === 0}
        >
          Print
        </Button>
      </Box>

      <Box sx={{ maxWidth: '8.5in', mx: 'auto', py: 3, '@media print': { maxWidth: 'none', py: 0 } }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error" className="no-print">{error}</Alert>}
        {!loading && !error && vendors.length === 0 && (
          <Alert severity="info" className="no-print">No complete registrations found.</Alert>
        )}
        {!loading && vendors.map((vendor, index) => (
          <VendorSheet
            key={vendor.vendor_id ?? vendor.email}
            vendor={vendor}
            index={index}
            total={vendors.length}
            festivalLabel={festivalLabel}
            eventDate={festival?.event_date}
          />
        ))}
      </Box>
    </Box>
  );
};

export default VendorPrintSheets;
